import { Role } from '@today/types'
import { z } from 'zod'

// ─── Public-facing schema (create / update inputs) ────────────────────────────

export const userCreateSchema = z.object({
	email: z.email(),
	displayName: z.string().min(1),
	isActive: z.boolean().default(true),
	// The tenant this user belongs to. A user belongs to exactly one tenant, so
	// it is part of identity. Optional on the wire (the route may inject it from
	// the caller's tenant context); the service requires a concrete value.
	tenantId: z.uuid().optional(),
	// Roles granted in the same provisioning transaction (TO-303 REQ-001).
	// MUST be declared here: zod strips unknown keys, so an undeclared `roles`
	// would be silently dropped and the grant would never happen. Constrained to
	// the Role enum, and further capped at the controller — requesting roles
	// needs ROLE_ASSIGN, and no caller may grant above their own authority.
	roles: z.array(z.enum(Role)).optional(),
	// The org unit to place the new user in as their primary unit (TO-435
	// REQ-006/REQ-007). Required for a unit-scoped caller, optional otherwise —
	// enforced at the controller, which knows the caller's scope.
	orgUnitId: z.uuid().optional(),
})

// A user's `tenantId` is fixed at creation — it is identity, not a mutable
// attribute — so omit it from the update surface.
//
// `roles` is omitted too: role changes go through the dedicated role-assignment
// routes, which carry the ROLE_ASSIGN gate and the authority ceiling. Leaving it
// on this schema would advertise a field PUT /users/{id} silently ignores, and
// would be a latent escalation for whoever later wires it up on a route gated
// only by USER_WRITE.
//
// `orgUnitId` is omitted as well: moving a user between units belongs to
// organization-service's assignment API, never a profile update (TO-435 REQ-007).
export const userUpdateSchema = userCreateSchema.omit({ tenantId: true, roles: true, orgUnitId: true }).partial()

export type UserCreateInput = z.infer<typeof userCreateSchema>
export type UserUpdateInput = z.infer<typeof userUpdateSchema>

// ─── Tenant-admin surface (TO-307) ───────────────────────────────────────────

/**
 * `POST /t/{slug}/admin/users` — invite. Carries no tenant: the tenant is the
 * scope resolved from the principal + slug, never the body. Granting `roles`
 * additionally needs `user.manage_roles` and is capped at the caller's authority.
 */
export const tenantUserInviteSchema = z.object({
	email: z.email(),
	displayName: z.string().min(1),
	roles: z.array(z.enum(Role)).optional(),
	/** Where to place the invitee (TO-435) — required for a unit-scoped caller. */
	orgUnitId: z.uuid().optional(),
})

/**
 * `PATCH /t/{slug}/admin/users/{userId}/roles` — the DESIRED role set, replacing
 * the current one. Non-empty: removing a user's access is deactivation, not an
 * empty role set.
 */
export const tenantUserRolesSchema = z.object({
	roles: z.array(z.enum(Role)).min(1),
})

/**
 * `PUT /t/{slug}/admin/users/{userId}/mfa-mandate` — the user's per-user MFA
 * mandate (TO-593 REQ-003). Strict: an extra field is a 422, never ignored.
 */
export const tenantUserMfaMandateSchema = z.object({ mandated: z.boolean() }).strict()

export type TenantUserInviteInput = z.infer<typeof tenantUserInviteSchema>
export type TenantUserRolesInput = z.infer<typeof tenantUserRolesSchema>
export type TenantUserMfaMandateInput = z.infer<typeof tenantUserMfaMandateSchema>

// ─── First-login surface (TO-309) ────────────────────────────────────────────

/** The emailed link token: 32 random bytes, base64url (43 characters). */
const invitationToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/)

/** `POST /t/{slug}/invitation/begin` and `/complete` — the link token only. */
export const invitationTokenSchema = z.object({ token: invitationToken })

/**
 * `POST /t/{slug}/invitation/accept` — the native first login. The password
 * policy itself is Cognito's; this only bounds the length (Cognito's own
 * 8–256 range) so an absurd body is refused before any IdP call.
 */
export const invitationAcceptSchema = z.object({
	token: invitationToken,
	newPassword: z.string().min(8).max(256),
})

export type InvitationTokenInput = z.infer<typeof invitationTokenSchema>
export type InvitationAcceptInput = z.infer<typeof invitationAcceptSchema>
